import { Injectable, inject } from '@angular/core';

import { distinctUntilChanged, map } from 'rxjs';
import { datadogRum } from '@datadog/browser-rum';
import { Store } from '@ngrx/store';

import { AppState, NativeBridgeService } from '@fcom/core';
import { LoginStatus } from '@fcom/core-api/login';
import { loginStatus, profileOrNot } from '@fcom/core/selectors';
import { LanguageService } from '@fcom/ui-translate';
import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';

@Injectable({ providedIn: 'root' })
export class DatadogContextService {
  private nativeBridge = inject(NativeBridgeService);
  private language = inject(LanguageService);
  private multivariateTest = inject(MultivariateTestService);
  private store = inject<Store<AppState>>(Store);

  /**
   * Attaches cross-cutting attributes to every RUM event so they can be faceted in Datadog. Keeps
   * them current as the customer switches language, their A/B segment resolves, or they log in / out
   * (which also updates their Finnair Plus tier).
   */
  trackGlobalContext(): void {
    datadogRum.setGlobalContextProperty('native_webview', this.nativeBridge.isInsideNativeWebView);

    this.language.locale
      .pipe(distinctUntilChanged())
      .subscribe((locale) => datadogRum.setGlobalContextProperty('locale', locale));

    this.language.lang
      .pipe(distinctUntilChanged())
      .subscribe((language) => datadogRum.setGlobalContextProperty('language', language));

    this.multivariateTest.segmentsFeed$
      .pipe(
        map((variant) => variant.segment),
        distinctUntilChanged()
      )
      .subscribe((segment) => datadogRum.setGlobalContextProperty('ab_segment', segment));

    this.store
      .pipe(
        loginStatus(),
        map((status) => status === LoginStatus.LOGGED_IN),
        distinctUntilChanged()
      )
      .subscribe((loggedIn) => datadogRum.setGlobalContextProperty('logged_in', loggedIn));

    this.store
      .pipe(
        profileOrNot(),
        map((customerProfile) => customerProfile?.tier ?? null),
        distinctUntilChanged()
      )
      .subscribe((tier) => datadogRum.setGlobalContextProperty('tier', tier));
  }
}
